/* Header com logo pera + LangSwitch + botão Logout + controles IA (Perfil, Templates, SOAP toggle, Voice) */
import React from 'react';
import { LangSwitch } from './LangSwitch';
import { CreditsBadge } from './CreditsBadge';
import VoiceButton from './VoiceButton';
import { LedPanel } from './LedPanel';
import type { VoiceState } from '../hooks/useVoicePairing';
import type { Locale, CreditsInfo, UserTier } from '../types';
import { ONBOARDING_RESTART_EVENT } from '../hooks/useOnboardingState';

interface HeaderProps {
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
  onLogout: () => void;
  creditsInfo: CreditsInfo | null;
  t: (key: string, vars?: Record<string, string | number>) => string;
  /** Se true, mostra badge de créditos, botão logout e controles IA */
  showUserControls?: boolean;
  /** Modo prontuário SOAP ligado/desligado */
  soapMode?: boolean;
  /** Callback do toggle SOAP */
  onToggleSoap?: () => void;
  /** Callback do botão Perfil */
  onOpenProfile?: () => void;
  /** Callback do botão Templates */
  onOpenTemplates?: () => void;
  /** Plantão ativo (memória 12h) */
  plantaoAtivo?: boolean;
  /**
   * Cronômetro regressivo HH:MM:SS — quando plantão ativo + valor não null,
   * substitui o texto "PLANTÃO ON" no botão. App.tsx computa via setInterval(1s).
   */
  plantaoCountdown?: string | null;
  /** Callback do toggle Plantão (iniciar/fechar) */
  onTogglePlantao?: () => void;
  /** Tier atual do user — necessário pro tier gate do voice */
  tier?: UserTier | null;
  /** Estado do voice (active = sessão ativa) */
  voiceActive?: boolean;
  /** Estado completo da máquina voice — mapeia LED variant no VoiceButton */
  voiceState?: VoiceState;
  /** Callback do click no VoiceButton */
  onVoiceClick?: () => void;
  /**
   * Trial v2 (02/07/2026) — link discreto "Já sou assinante? Entrar" → /login.
   * Sempre visível pro visitante anônimo (App só passa true no branch anônimo).
   */
  showTrialLogin?: boolean;
  /**
   * Trial v2 (02/07/2026) — botão VOZ pro anônimo com trial ativo.
   * Usa os mesmos voiceActive/voiceState/onVoiceClick; tier null (sem lock).
   */
  showTrialVoice?: boolean;
}

export function Header({
  locale,
  onLocaleChange,
  onLogout,
  creditsInfo,
  t,
  showUserControls = false,
  soapMode = false,
  onToggleSoap,
  onOpenProfile,
  onOpenTemplates,
  plantaoAtivo = false,
  plantaoCountdown = null,
  onTogglePlantao,
  tier,
  voiceActive = false,
  voiceState = 'idle',
  onVoiceClick,
  showTrialLogin = false,
  showTrialVoice = false,
}: HeaderProps) {

  // Kit Claude Design — repouso limpo (sem neon), Plus Jakarta Sans legível.
  // Glow neon fica reservado ao :hover (classe .ia-hdr-btn no hover-glow.css).
  const btnBase: React.CSSProperties = {
    fontFamily: 'var(--font-sans)',
    fontSize: '12px',
    fontWeight: 600,
    padding: '0 14px',
    background: 'transparent',
    border: '1px solid rgba(124, 124, 248, 0.30)',
    color: 'var(--ink-light)',
    cursor: 'pointer',
    letterSpacing: '0.01em',
    textShadow: 'none',
    boxShadow: 'none',
    transition: 'border-color 180ms ease-out, box-shadow 180ms ease-out, color 180ms ease-out',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    minHeight: '46px',
    minWidth: '104px',
    boxSizing: 'border-box',
  };
  // Subtítulo Plus Jakarta Sans sutil dentro de botões (apenas Perfil por enquanto)
  const btnSubLabel: React.CSSProperties = {
    fontFamily: 'var(--font-sans)',
    fontSize: '11px',
    fontWeight: 'normal',
    color: 'rgba(124, 124, 248, 0.65)',
    textShadow: 'none',
    letterSpacing: '0.02em',
    textTransform: 'lowercase',
  };
  // Estado ATIVO — acento violeta preenchido (sem neon em repouso)
  const btnActive: React.CSSProperties = {
    ...btnBase,
    color: '#FFFFFF',
    border: '1px solid var(--violet)',
    background: 'rgba(124, 124, 248, 0.14)',
    boxShadow: '0 0 0 1px rgba(124, 124, 248, 0.25)',
  };
  return (
    <header
      style={{
        borderBottom: '1px solid var(--ink-soft)',
        background: 'var(--ink)',
        padding: '12px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        flexWrap: 'wrap',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}
    >
      {/* Logo + título — 20/05/2026: logo agora link pra drpera.com.br */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {/* Mascote pera Pixar — ícone universal do projeto. Clicável → home site. */}
        <a
          href="https://drpera.com.br"
          aria-label="Voltar para drpera.com.br"
          title="Voltar para drpera.com.br"
          style={{ display: 'inline-block', lineHeight: 0, cursor: 'pointer' }}
        >
          <img
            src="/icons/icon-48.png"
            alt="Mascote Dr. Pera"
            width={36}
            height={36}
            style={{
              display: 'block',
              // Mascote redondo coral — borderRadius 50% reforça recorte circular
              // mesmo se imagem futura vier com bordas brancas (defesa).
              borderRadius: '50%',
              filter: 'drop-shadow(0 0 6px rgba(124, 124, 248, 0.55))',
              transition: 'filter 180ms ease-out',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.filter = 'drop-shadow(0 0 10px rgba(0, 255, 65, 0.9)) drop-shadow(0 0 20px rgba(0, 255, 65, 0.5))')}
            onMouseLeave={(e) => (e.currentTarget.style.filter = 'drop-shadow(0 0 6px rgba(124, 124, 248, 0.55))')}
          />
        </a>
        <div>
          <div
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: '15px',
              fontWeight: 700,
              color: '#FFFFFF',
              textShadow: 'none',
              letterSpacing: '0.01em',
            }}
          >
            {t('header.brand')}
          </div>
          <div
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: '12px',
              color: 'var(--ink-mute)',
              letterSpacing: '0.01em',
            }}
          >
            {t('header.subtitle')}
          </div>
        </div>
      </div>

      {/* Controles direita */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
        {showUserControls && (
          <>
            {/* Ordem 20/05/2026: PERFIL · PLANTÃO · SOAP · VOZ · PREÇOS.
                TEMPLATES oculto (mantida prop onOpenTemplates pra reincorporar
                no futuro sem refazer). */}

            {/* Botão Perfil IA — com subtítulo "configurações/idioma" em Plus Jakarta Sans sutil */}
            <button
              type="button"
              className="ia-hdr-btn"
              data-onboarding="header-profile"
              onClick={onOpenProfile}
              aria-label={t('header.profile')}
              style={btnBase}
            >
              <span>◉ {t('header.profile')}</span>
              <span style={btnSubLabel}>{t('header.profileSubtitle')}</span>
            </button>

            {/* Toggle Plantão (12h memória) — KR LEDs verde quando ativo
                Texto sempre "Plantão" (label do mockup 22/05); countdown vira sub-texto
                inline quando ativo. LEDs encodam ON/OFF visualmente. */}
            <button
              type="button"
              className="ia-hdr-btn"
              data-onboarding="header-plantao"
              onClick={onTogglePlantao}
              aria-label={plantaoAtivo ? t('header.plantaoFechar') : t('header.plantaoIniciar')}
              aria-pressed={plantaoAtivo}
              style={
                plantaoAtivo
                  ? { ...btnActive, flexDirection: 'row', gap: 0 }
                  : { ...btnBase, flexDirection: 'row', gap: 0 }
              }
              title={plantaoAtivo ? t('header.plantaoTooltipFechar') : t('header.plantaoTooltipIniciar')}
            >
              <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <span>⏱ {t('header.plantaoIniciar')}</span>
                {plantaoAtivo && plantaoCountdown !== null && plantaoCountdown !== '' && (
                  <span
                    style={{
                      fontFamily: 'var(--font-sans)',
                      fontSize: '10px',
                      color: '#00D4FF',
                      textShadow:
                        '0 0 6px rgba(0, 212, 255, 0.75), 0 0 14px rgba(0, 212, 255, 0.35)',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {plantaoCountdown}
                  </span>
                )}
              </span>
              <LedPanel variant={plantaoAtivo ? 'green' : 'off'} />
            </button>

            {/* Toggle SOAP on/off — KR LEDs verde quando ON */}
            <button
              type="button"
              className="ia-hdr-btn"
              data-onboarding="header-soap"
              onClick={onToggleSoap}
              aria-label={t('header.soap')}
              aria-pressed={soapMode}
              style={
                soapMode
                  ? { ...btnActive, flexDirection: 'row', gap: 0 }
                  : { ...btnBase, flexDirection: 'row', gap: 0 }
              }
              title={soapMode ? t('header.soapOn') : t('header.soapOff')}
            >
              <span>⊕ SOAP {soapMode ? 'ON' : 'OFF'}</span>
              <LedPanel variant={soapMode ? 'green' : 'off'} />
            </button>

            {/* VOZ — ditado por voz via QR pairing (Fase Voice S2)
                Sempre visível pra mostrar a feature, mas trava com 🔒 se free/plantonista */}
            <VoiceButton
              tier={tier ?? null}
              active={voiceActive}
              voiceState={voiceState}
              onClick={onVoiceClick ?? (() => {})}
              t={t}
            />

            {/* PREÇOS — CTA pra trial/free users irem comprar (20/05/2026).
                Destaque visual: border + glow coral em vez de verde, pra puxar atenção. */}
            <a
              href="https://drpera.com.br/precos"
              target="_blank"
              rel="noopener noreferrer"
              className="ia-hdr-btn"
              aria-label={t('header.precos', { defaultValue: 'Ver planos e preços' })}
              title={t('header.precos', { defaultValue: 'Ver planos e preços' })}
              style={{
                ...btnBase,
                textDecoration: 'none',
                border: '1px solid var(--cta-blue)',
                color: '#7FB0FF',
              }}
            >
              💎 {t('header.precos', { defaultValue: 'PREÇOS' })}
            </a>

            {/* TEMPLATES — OCULTO 20/05/2026. Mantida prop onOpenTemplates +
                TemplatesModal em App.tsx pra reincorporar futuramente.
            <button
              type="button"
              data-onboarding="header-templates"
              onClick={onOpenTemplates}
              aria-label={t('header.templates')}
              style={btnBase}
            >
              ▤ {t('header.templates')}
            </button>
            */}

            <CreditsBadge creditsInfo={creditsInfo} t={t} />

            {/* HELP "?" — reabre o onboarding tour (briefing 23/05/2026 noite).
                Dispara CustomEvent global escutado pelo hook useOnboardingState
                no OnboardingTour. Zero prop drilling. */}
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent(ONBOARDING_RESTART_EVENT))}
              aria-label={t('header.helpTour', { defaultValue: 'Reabrir tutorial' })}
              title={t('header.helpTour', { defaultValue: 'Reabrir tutorial' })}
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: '14px',
                fontWeight: 600,
                width: '34px',
                height: '34px',
                padding: 0,
                background: 'transparent',
                border: '1px solid rgba(124, 124, 248, 0.30)',
                borderRadius: '50%',
                color: 'var(--ink-light)',
                cursor: 'pointer',
                textShadow: 'none',
                boxShadow: 'none',
                transition:
                  'box-shadow 180ms ease-out, transform 120ms ease-out, color 180ms ease-out, border-color 180ms ease-out',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--violet)';
                e.currentTarget.style.color = '#fff';
                e.currentTarget.style.boxShadow =
                  '0 0 12px rgba(0, 255, 65, 0.6), 0 0 26px rgba(0, 255, 65, 0.3)';
                e.currentTarget.style.transform = 'scale(1.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(124, 124, 248, 0.30)';
                e.currentTarget.style.color = 'var(--ink-light)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              ?
            </button>

            {/* SAIR — coral sutil pra ser visível mas não competir com PREÇOS */}
            <button
              type="button"
              className="ia-hdr-btn"
              onClick={onLogout}
              aria-label={t('chat.logout')}
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: '12px',
                fontWeight: 600,
                padding: '8px 13px',
                background: 'transparent',
                border: '1px solid rgba(199, 200, 206, 0.35)',
                color: 'var(--ink-light)',
                cursor: 'pointer',
                letterSpacing: '0.01em',
                textShadow: 'none',
                boxShadow: 'none',
                transition: 'border-color 180ms ease-out, box-shadow 180ms ease-out, color 180ms ease-out',
                whiteSpace: 'nowrap',
                minHeight: '46px',
                boxSizing: 'border-box',
              }}
            >
              {t('chat.logout')}
            </button>
          </>
        )}

        {/* Trial v2 — VOZ pro anônimo com trial ativo (02/07/2026).
            tier null = sem lock; backend decide (403 trial_ended/email_required
            roteados pelo App). Fluxo dos pagos intocado (bloco acima). */}
        {!showUserControls && showTrialVoice && (
          <VoiceButton
            tier={null}
            active={voiceActive}
            voiceState={voiceState}
            onClick={onVoiceClick ?? (() => {})}
            t={t}
          />
        )}

        {/* Trial v2 — link discreto SEMPRE visível pro anônimo:
            "Já sou assinante? Entrar" → /login (re-entrada forçada, App.tsx). */}
        {!showUserControls && showTrialLogin && (
          <a
            href="/login"
            aria-label={`${t('trial.login.question')} ${t('trial.login.cta')}`}
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: '12px',
              color: 'var(--ink-mute)',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              letterSpacing: '0.01em',
              padding: '4px 6px',
              cursor: 'pointer',
            }}
          >
            {t('trial.login.question')}{' '}
            <span
              style={{
                color: 'var(--ink-light)',
                fontWeight: 600,
                textDecoration: 'underline',
                textUnderlineOffset: '3px',
              }}
            >
              {t('trial.login.cta')}
            </span>
          </a>
        )}

        <LangSwitch locale={locale} onLocaleChange={onLocaleChange} />
      </div>
    </header>
  );
}
